import { createStore, type StoreApi } from 'zustand';

/**
 * Per-conversation Zustand store for the "Add references" URL-entry session.
 *
 * A single action sheet posts formatted `reference` messages into the
 * conversation, so at most one session is open at a time. The store tracks the
 * URLs in the form, the session status, and any transient error from the
 * format-batch call. There is no per-message binding: this entry point is
 * always proactive (toolbar-initiated) and never writes resolution back to a
 * chat bubble.
 */

export const ADD_REFERENCES_SESSION_STATUS = {
  /** No URL-entry session in progress. */
  IDLE: 'idle',
  /** Action sheet open, expert is typing URLs. */
  ENTERING_URLS: 'entering_urls',
  /** URLs submitted; waiting on the batch-format API. */
  FORMATTING: 'formatting',
} as const;

export type AddReferencesSessionStatus =
  (typeof ADD_REFERENCES_SESSION_STATUS)[keyof typeof ADD_REFERENCES_SESSION_STATUS];

type AddReferencesState = {
  /** Current session status. */
  status: AddReferencesSessionStatus;
  /** URLs currently in the entry form (one entry per input row). */
  urls: string[];
  /** Error message from the last format-batch call, if any. */
  errorMessage: string | null;
  /**
   * Identity keys of references already posted in this session. Retained
   * across a partial-failure error so a retry re-formats the same URLs but
   * skips the references that already landed, rather than double-posting
   * them. Cleared when a fresh session starts or the session resets.
   */
  sentReferenceKeys: string[];
};

type AddReferencesActions = {
  /** Open the URL-entry session. */
  openEntry: () => void;
  /** Close the session WITHOUT submitting. */
  closeEntry: () => void;
  /** Replace the list of URLs in the entry form. */
  setUrls: (urls: string[]) => void;
  /** Convenience: add an empty row. */
  addUrlRow: () => void;
  /** Convenience: update the URL at a given index. */
  setUrlAt: (index: number, url: string) => void;
  /** Convenience: remove the URL at a given index. */
  removeUrlAt: (index: number) => void;
  /** Transition to formatting (disables the action sheet). */
  markFormatting: () => void;
  /** Record a format-batch failure so the sheet can show retry. */
  setError: (message: string) => void;
  /** Append reference identity keys that were successfully posted, so a
   * retry after a partial failure does not re-post them. */
  markReferencesSent: (keys: string[]) => void;
  /** Reset everything to initial state. Called after a successful send. */
  reset: () => void;
};

export type AddReferencesStore = AddReferencesState & AddReferencesActions;

const INITIAL_STATE: AddReferencesState = {
  status: ADD_REFERENCES_SESSION_STATUS.IDLE,
  urls: [''],
  errorMessage: null,
  sentReferenceKeys: [],
};

export function createAddReferencesStore(): StoreApi<AddReferencesStore> {
  return createStore<AddReferencesStore>((set, get) => ({
    ...INITIAL_STATE,

    openEntry: () =>
      set({
        status: ADD_REFERENCES_SESSION_STATUS.ENTERING_URLS,
        urls: [''],
        errorMessage: null,
        sentReferenceKeys: [],
      }),

    closeEntry: () => set(INITIAL_STATE),

    setUrls: (urls) => set({ urls }),

    addUrlRow: () => set({ urls: [...get().urls, ''] }),

    setUrlAt: (index, url) => {
      const next = [...get().urls];

      next[index] = url;

      set({ urls: next });
    },

    removeUrlAt: (index) => {
      const next = get().urls.filter((_, currentIndex) => currentIndex !== index);

      set({ urls: next.length > 0 ? next : [''] });
    },

    markFormatting: () =>
      set({
        status: ADD_REFERENCES_SESSION_STATUS.FORMATTING,
        errorMessage: null,
      }),

    setError: (message) =>
      set({
        status: ADD_REFERENCES_SESSION_STATUS.ENTERING_URLS,
        errorMessage: message,
      }),

    markReferencesSent: (keys) => set({ sentReferenceKeys: [...get().sentReferenceKeys, ...keys] }),

    reset: () => set(INITIAL_STATE),
  }));
}

const storeMap = new Map<string, StoreApi<AddReferencesStore>>();

export function getAddReferencesStore(conversationId: string): StoreApi<AddReferencesStore> {
  let store = storeMap.get(conversationId);

  if (!store) {
    store = createAddReferencesStore();

    storeMap.set(conversationId, store);
  }

  return store;
}

export function deleteAddReferencesStore(conversationId: string) {
  storeMap.delete(conversationId);
}
